import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, of, switchMap, tap } from 'rxjs';

import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ENV_CONFIG } from '@ps/core-config';
import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';

import { ReportService } from '../../services/report.service';
import { SurveyPerformanceReportService } from '../../services/survey-performance-report.service';
import { TransactionStatusService } from '../../services/transaction-status.service';
import {
  ReportActions,
  SurveyPerformanceReportActions,
} from '../actions/report.actions';

@Injectable()
export class ReportEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _authFacade = inject(AuthSelectorsFacade);
  private readonly _document = inject(DOCUMENT);
  private readonly _environmentConfig = inject(ENV_CONFIG);
  private readonly _reportService = inject(ReportService);
  private readonly _sprService = inject(SurveyPerformanceReportService);
  private readonly _statusService = inject(TransactionStatusService);
  private readonly _toastService = inject(ToastService);

  public loadReports$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ReportActions.loadReports),
      switchMap(({ userId }) =>
        this._reportService.list(userId).pipe(
          map((reports) => ReportActions.loadReportsSuccess({ reports })),
          this.psCatchError(
            (error) =>
              of(
                ReportActions.loadReportsFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadStatuses$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ReportActions.loadStatuses),
      switchMap(() =>
        this._statusService.list().pipe(
          map((statuses) => ReportActions.loadStatusesSuccess({ statuses })),
          this.psCatchError(
            (error) =>
              of(
                ReportActions.loadStatusesFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public requestReport$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ReportActions.requestReport),
      switchMap(({ request }) =>
        this._reportService.request(request).pipe(
          map((msg) =>
            ReportActions.requestReportSuccess({ userId: request.userId, msg }),
          ),
          this.psCatchError(
            (error) =>
              of(
                ReportActions.requestReportFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public requestReportSuccessToast$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ReportActions.requestReportSuccess),
        tap(({ msg }) => this._toastService.success(msg)),
      ),
    { dispatch: false },
  );

  public reloadAfterRequest$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ReportActions.requestReportSuccess),
      map(({ userId }) => ReportActions.loadReports({ userId })),
    ),
  );

  public downloadReport$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ReportActions.downloadReport),
      switchMap(({ filename, fileType }) =>
        this._reportService.downloadUrl(filename, fileType).pipe(
          map((url) => ReportActions.downloadReportSuccess({ url })),
          this.psCatchError(
            (error) =>
              of(
                ReportActions.downloadReportFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public downloadUserActivityReport$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ReportActions.downloadUserActivityReport),
      switchMap(() =>
        this._reportService.userActivityReportFilename().pipe(
          map((filename) =>
            ReportActions.downloadUserActivityReportSuccess({ filename }),
          ),
          this.psCatchError(
            (error) =>
              of(
                ReportActions.downloadUserActivityReportFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public openDownloadWindow$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ReportActions.downloadReportSuccess),
        tap(({ url }) => this._document.defaultView?.open(url, '_blank')),
      ),
    { dispatch: false },
  );

  public downloadUserActivityReportFile$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ReportActions.downloadUserActivityReportSuccess),
        tap(({ filename }) => {
          const token = this._authFacade
            .user()
            .map((user) => user.token)
            .unwrapOr('');
          const baseUrl = this._environmentConfig.pureSpectrum.url;
          this._document.location.assign(
            `${baseUrl}/reports/download-report/${filename}?access_token=${token}`,
          );
        }),
      ),
    { dispatch: false },
  );

  public loadBuyers$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyPerformanceReportActions.loadBuyers),
      switchMap(() =>
        this._sprService.buyers().pipe(
          map((buyers) =>
            SurveyPerformanceReportActions.loadBuyersSuccess({ buyers }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyPerformanceReportActions.loadBuyersFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadResults$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyPerformanceReportActions.loadResults),
      switchMap(({ filters }) =>
        this._sprService.results(filters).pipe(
          map((results) =>
            SurveyPerformanceReportActions.loadResultsSuccess({ results }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyPerformanceReportActions.loadResultsFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );
}
